import { orgAPI } from "@/api/api";
import type {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type { MicrosoftTeamsMessageEntity } from "@cohesive/api-entities";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

type MicrosoftTeamsChatSearchResponse = ExecuteToolboxResponse<
  MicrosoftTeamsMessageEntity[]
>;

interface UseMicrosoftTeamsChatSearchProps {
  connection: IntegrationConnection;
  query: string;
  limit?: number;
}

export function useMicrosoftTeamsChatSearch(
  { connection, query, limit = 20 }: UseMicrosoftTeamsChatSearchProps,
  options?: Partial<UseQueryOptions<MicrosoftTeamsChatSearchResponse, Error>>,
) {
  return useQuery<MicrosoftTeamsChatSearchResponse>({
    queryKey: ["microsoft-teams-chat-search", connection.id, query, limit],
    queryFn: async () => {
      if (!query) return { data: [] };
      // Typed entities drive the rich message rows; their generic projections
      // ride along in data_generic and are what get placed on the canvas.
      return orgAPI.toolbox.execute<
        MicrosoftTeamsMessageEntity[],
        { query: string; limit?: number }
      >({
        integration_connection_id: connection.id,
        action_name: "microsoft-teams-search-messages",
        action_input: { query, limit },
        entity_format: "both",
      });
    },
    enabled: !!connection.integration_id && !!query,
    staleTime: 1000 * 30,
    ...options,
  });
}
